import type { TransactionContext } from "../../transaction-context";
import type { StripeSubscriptionPlanKey } from "../stripe-types";
import type { SubscriptionTier } from "./constants";
import type { TierLimits } from "./limits";
import { SUBSCRIPTION_TIERS } from "./constants";

export const POSTPAID_COLLECTION_MODES = {
  AUTOMATIC: "automatic",
  OFF_PLATFORM: "off_platform",
} as const;

export type PostpaidCollectionMode =
  (typeof POSTPAID_COLLECTION_MODES)[keyof typeof POSTPAID_COLLECTION_MODES];

export interface EffectiveSubscription {
  tier: SubscriptionTier;
  trialActive: boolean;
  trialEndsAt?: Date;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  postpaidEnabled: boolean;
  postpaidCollectionMode: PostpaidCollectionMode;
  postpaidReason?: string;
  postpaidUpdatedAt?: Date;
  /**
   * Per-team post-paid exposure cap in nanocredits. Undefined when never
   * explicitly configured; exposure enforcement falls back to the default cap.
   */
  postpaidExposureLimitNanocredits?: bigint;
}

export type EffectivePlan =
  | {
      kind: "tier";
      tier: SubscriptionTier;
      source: "billing" | "trial" | "manual_override";
      trialActive?: boolean;
      trialEndsAt?: Date;
    }
  | {
      kind: "custom";
      planId: string;
      version: number;
      planName: string;
      limits: TierLimits;
    };

/**
 * Resolve an effective plan to a SubscriptionTier for tier-keyed lookups
 * (entitlements, included allowances, display). Custom plans carry their own
 * limits and bypass tier tables, so they map to ENTERPRISE — the most
 * permissive standard column — wherever a tier key is required.
 */
export function getEffectivePlanTier(plan: EffectivePlan): SubscriptionTier {
  return plan.kind === "tier" ? plan.tier : SUBSCRIPTION_TIERS.ENTERPRISE;
}

/**
 * Total ordering of the standard tiers (FREE < GROWTH < ENTERPRISE) for
 * comparisons like "is this tier strictly higher than that one". Custom plans
 * are not ranked here — resolve them with {@link getEffectivePlanTier} first.
 */
const SUBSCRIPTION_TIER_RANK: Readonly<Record<SubscriptionTier, number>> = {
  [SUBSCRIPTION_TIERS.FREE]: 0,
  [SUBSCRIPTION_TIERS.GROWTH]: 1,
  [SUBSCRIPTION_TIERS.ENTERPRISE]: 2,
};

/** True when `candidate` sits strictly above `baseline` in the tier ranking. */
export function isTierAbove(
  candidate: SubscriptionTier,
  baseline: SubscriptionTier,
): boolean {
  return SUBSCRIPTION_TIER_RANK[candidate] > SUBSCRIPTION_TIER_RANK[baseline];
}

export interface GetEffectivePlanInput {
  teamId: string;
  ctx?: TransactionContext;
}

export interface GetEffectiveSubscriptionInput {
  teamId: string;
}

export interface SetSubscriptionInput {
  teamId: string;
  tier: SubscriptionTier;
  planKey?: StripeSubscriptionPlanKey | null;
  stripeSubscriptionId?: string | null;
  ctx?: TransactionContext;
}

export interface SetTrialInput {
  teamId: string;
  tier: SubscriptionTier;
  trialEndsAt: Date;
}

export interface SetManualTierOverrideInput {
  teamId: string;
  tier: SubscriptionTier;
  reason?: string;
}

export interface ClearManualTierOverrideInput {
  teamId: string;
}

/**
 * Provider-neutral collectability gate used for the enablement decision.
 * Provider-specific readiness snapshots should be translated into this shape
 * at the boundary that owns that provider.
 */
export interface PostpaidBillingCollectabilityGate {
  ready: boolean;
  hasBillingCustomer: boolean;
  hasDefaultPaymentMethod: boolean;
}

export interface SetPostpaidBillingInput {
  teamId: string;
  enabled: boolean;
  /** Omitted inputs preserve the team's existing collection mode. */
  collectionMode?: PostpaidCollectionMode;
  reason?: string;
  /**
   * Admin-supplied exposure cap in nanocredits. Persisted whenever an explicit
   * value is passed — including on disable, so a cap lowered alongside a
   * disable survives a later re-enable. When enabling and omitted, the default
   * cap is stored. `null` is ignored (keeps the existing/default cap).
   */
  exposureLimitNanocredits?: bigint | null;
  /**
   * Explicit, audited override allowing post-paid to be enabled for a team
   * that is not currently collectable.
   */
  overrideUncollectable?: boolean;
  /**
   * Required to newly enable post-paid without an override; the service throws
   * `PostpaidEnablementNotReadyError` when it is missing or not ready.
   */
  collectability?: PostpaidBillingCollectabilityGate;
}

export interface PostpaidExposureComponents {
  unbilledAmountNanocredits: bigint;
  outstandingInvoiceDebtNanocredits: bigint;
  /** Invoices in `open`/`payment_failed` contributing to the debt above. */
  outstandingInvoiceCount: number;
}

export type PostpaidExposureStatus =
  | "within_limit"
  | "near_limit"
  | "over_limit";

/**
 * A team's unpaid post-paid exposure at a point in time: unbilled usage plus
 * outstanding invoice debt, net of positive credits, compared against the
 * team's configured exposure cap.
 */
export interface PostpaidExposureSnapshot extends PostpaidExposureComponents {
  availableCreditNanocredits: bigint;
  currentExposureNanocredits: bigint;
  limitNanocredits: bigint;
  status: PostpaidExposureStatus;
}

export interface GetPostpaidExposureSnapshotInput {
  teamId: string;
}
